/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyView from "../../components/EmptyView";
import Stack from "../../components/ui/Stack";
import Icon from "../../components/ui/Icon";
import Paginator from "../../components/ui/Paginator";
import Dimmed from "../../components/ui/Dimmed";
import ClientRecurringProfileItem from "../../components/client/ClientRecurringProfileItem";
import { ROUTES } from "../../constants/constants";
import { useRecurringProfiles } from "../../lib/react-query/useRecurringProfiles";

const ClientRecurringProfiles = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isFetching, isPlaceholderData } = useRecurringProfiles(currentPage, {
    placeholderData: keepPreviousData,
  });

  const profiles = data?.data || [];
  const totalPages = data?.metadata?.pages_count || 1;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container>
      <NavigationHeader
        title="Recurring profiles"
        subtitle="Your subscriptions and automatic payments"
        back={ROUTES.CLIENT_PAYMENTS}
      />

      {isLoading && <LoadingIndicator page />}

      {!isLoading && profiles.length === 0 && (
        <EmptyView icon={Icon.RecurringCharge} description="You don't have any recurring profiles yet" />
      )}

      {!isLoading && profiles.length > 0 && (
        <Stack $gap="1rem">
          <Dimmed $active={isPlaceholderData}>
            <Stack $gap="1rem">
              {profiles.map((profile) => (
                <ClientRecurringProfileItem key={profile.id} profile={profile} />
              ))}
            </Stack>
          </Dimmed>
          {totalPages > 1 && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isLoading={isFetching}
            />
          )}
        </Stack>
      )}
    </Container>
  );
};

export default ClientRecurringProfiles;
